import { Box, Fade } from '@mui/material';
import { styled } from '@mui/material/styles';
import { selectTileDimensions } from 'features/layout/features/dimensions/dimensionsSlice';
import { selectPageStreams } from 'features/layout/features/pagination/paginationSlice';
import { selectCustomTiles } from 'features/layout/features/config/configSlice';
import { selectGridPanel } from 'features/layout/features/content/contentSlice';
import { selectIsHidingRolesInRecording } from 'features/layout/features/config/systemSessionSelectors';
import { GridPlaceholder } from 'features/layout/GridPlaceholder';
import PagedTile from 'features/layout/tiles/PagedTile';
import CustomTile from 'features/layout/tiles/CustomTile';
import { useHoverParent } from 'features/layout/useHoverParent';
import { makeSourceKey } from 'features/streaming/utils';
import React, { useMemo } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useAppSelector } from 'store/hooks';

const TileContainer = styled(TransitionGroup)<{
  cols: number;
  rows: number;
}>(({ cols, rows }) => ({
  width: '100%',
  height: '100%',
  display: 'grid',
  gridTemplateColumns: `repeat(${cols || 1}, 1fr)`,
  gridTemplateRows: `repeat(${rows || 1}, 1fr)`,
}));

export const PagedTiles = () => {
  const { cols, rows } = useAppSelector(selectTileDimensions);
  const streams = useAppSelector(selectPageStreams);
  const customTiles = useAppSelector(selectCustomTiles);
  const activePanel = useAppSelector(selectGridPanel);
  const hidingRolesInRecording = useAppSelector(selectIsHidingRolesInRecording);

  const tiles = useMemo(
    () =>
      streams.map((source) => (
        <Fade
          key={makeSourceKey(source)}
          timeout={source.kind === 'local' ? 0 : { enter: 350, exit: 0 }}
        >
          <Box sx={{ display: 'flex' }}>
            <PagedTile source={source} />
          </Box>
        </Fade>
      )),
    [streams]
  );

  const firstCustomTiles = useMemo(
    () =>
      customTiles.first.map((tile) => (
        <Fade key={tile.name} timeout={{ enter: 350, exit: 0 }}>
          <Box sx={{ display: 'flex' }}>
            <CustomTile data={tile} />
          </Box>
        </Fade>
      )),
    [customTiles.first]
  );

  const lastCustomTiles = useMemo(
    () =>
      customTiles.last.map((tile) => (
        <Fade key={tile.name} timeout={{ enter: 350, exit: 0 }}>
          <Box sx={{ display: 'flex' }}>
            <CustomTile data={tile} />
          </Box>
        </Fade>
      )),
    [customTiles.last]
  );

  const { className, onMouseEnter, onMouseMove, onMouseLeave } = useHoverParent('tiles');
  const mouseCallbacks = { onMouseEnter, onMouseMove, onMouseLeave };

  const hasNoStreams = !streams.length && !customTiles.first.length && !customTiles.last.length;

  if (hasNoStreams && (activePanel || hidingRolesInRecording)) {
    return null;
  }

  if (hasNoStreams) {
    return (
      <Box
        {...mouseCallbacks}
        sx={{
          width: '100%',
          height: '100%',
          display: 'grid',
          gridTemplateColumns: `repeat(1, 1fr)`,
          gridTemplateRows: `repeat(1, 1fr)`,
        }}
      >
        <GridPlaceholder />
      </Box>
    );
  }

  return (
    <TileContainer className={className} {...mouseCallbacks} cols={cols} rows={rows}>
      {firstCustomTiles}
      {tiles}
      {lastCustomTiles}
    </TileContainer>
  );
};
